import { IOption } from "../../components/inputs/select-dropdown/select-dropdown"
import { IResolution, IResolutionCategory, RESOLUTION_CATEGORY } from "../../models/ResolutionTypes"
import { INSTANT_REFUND_PROVIDER, RESOLUTION_ID, RESOLUTION_TYPE } from "../resolutions/constants"
import { APPLY_ON, APPROVAL_PROCESS, AUTO_MARK_DONE, AUTO_PROCESS_EXECUTE, CHARGE_FEES, SINCE_UNIT, USED_FOR_OPTIONS } from "./constants"
import { IShippingConnection } from "../../models/IntegrationsTypes"
import { IAggregatorInfo, IDropOffSettings, IReturnMethod } from "../return-methods/return-method-types"
import { IReturnLabelSettings, RETURN_METHOD_TYPE } from "../return-methods/return-method-types"
import { AUTO_PROCESS_TRIGGER, AUTO_PROCESS_VALIDATED_QUALIFIER, RETURN_LABEL_FEE_PAYMENT } from "../../models/User"
import { NOTE_TYPE } from "../../components/note/note"
import { ICheckboxesTreeItem } from "../../components/inputs/checkboxes-tree-dropdown/checkboxes-tree/stores/checkboxes-tree-store"


export interface IPolicyRule {
    id: number,
    name: string,
    charge_fees: CHARGE_FEES,
    resolution_id: number,
    return_method_id: number,
    limit_eligible_items_order_level: boolean,
    limit_eligible_items_customer_level: boolean,
    limit_eligible_items_rma_level: boolean,
    limit_eligible_items: boolean,
    is_enabled: boolean,
    is_auto_approved: APPROVAL_PROCESS,
    is_auto_exchange: boolean,
    auto_exchange_trigger: AUTO_PROCESS_TRIGGER|null,
    auto_exchange_validated_qualifier?: AUTO_PROCESS_VALIDATED_QUALIFIER|null,
    auto_exchange_execute: AUTO_PROCESS_EXECUTE|null,
    auto_exchange_since_value?: number|null,
    auto_exchange_since_unit?: SINCE_UNIT|null,
    is_auto_refund: boolean,
    auto_refund_trigger: AUTO_PROCESS_TRIGGER|null,
    auto_refund_validated_qualifier?: AUTO_PROCESS_VALIDATED_QUALIFIER|null,
    auto_refund_execute: AUTO_PROCESS_EXECUTE|null,
    auto_refund_since_value?: number|null,
    auto_refund_since_unit?: SINCE_UNIT|null,
    issue_partial_refund: boolean,
    auto_mark_done: AUTO_MARK_DONE,
    auto_mark_done_after_partial_validation: boolean,
    auto_mark_done_execute: AUTO_PROCESS_EXECUTE | null,
    auto_mark_done_since_value: number | null,
    auto_mark_done_since_unit: SINCE_UNIT | null,
    auto_mark_done_trigger: string | null,
    display_fee_option: DISPLAY_FEE_OPTION,
    policy_rule_fees: IPolicyFee[],
    policy_rule_condition_groups: IPolicyConditionGroup[],
    policy_rule_condition_groups_customer_level: IPolicyConditionGroup[],
    policy_rule_condition_groups_order_level: IPolicyConditionGroup[],
    policy_rule_condition_groups_rma_level: IPolicyConditionGroup[],
    include_shipping_amount_in_refund: boolean,
    refund_product_duties: boolean,
    display_as_disabled: boolean,
    return_label_fee_payment: RETURN_LABEL_FEE_PAYMENT,
    apply_on: APPLY_ON,
    enable_reserve_items: boolean,
    restock_time: string|null,
    item_reservation_timeframe: number|null,
    require_address: boolean,
    prefill_address: boolean,
    address_requirements: IPolicyAddressRequirements,
    is_coverage: boolean,
    include_eu_right_of_withdrawal_email: boolean,
    show_cancellation_reason_step: boolean | null,

    instant_exchange_enabled: boolean,
    capture_payment_trigger: AUTO_PROCESS_TRIGGER,
    capture_payment_validated_qualifier: AUTO_PROCESS_VALIDATED_QUALIFIER|null,

    policy_rule_availability?: IPolicyRuleAvailability[], /** this array is not optional, but added optional for BC for orthofeet crash, orthofeet always deployed later than prod */
    request_multiple_shipments: boolean,

    notify_helpdesk: boolean,
    _has_assigned_product_cases: boolean,

    item_ineligibility_reason_key?: string | null,
    _item_ineligibility_reason_source_key?: string | null,

    _has_changes?: boolean,
}

export const POLICY_ITEM_INELIGIBILITY_REASON_KEY_PREFIX =
    'POLICY_RULE_ITEM_INELIGIBILITY_REASON';

export function buildPolicyItemIneligibilityReasonClientKey(clientId: string): string {
    return `${POLICY_ITEM_INELIGIBILITY_REASON_KEY_PREFIX}_#${clientId.split('-')[4]}`;
}

export interface IPolicyTranslationCopyPair {
    sourceKey: string;
    targetKey: string;
}

/**
 * Persisted policy attribute paired with RMS metadata that records the
 * localization source when a policy is duplicated.
 *
 * Add an entry here when a new localized policy field follows the same
 * version/duplicate key lifecycle.
 */
export const LOCALIZED_POLICY_FIELD_SOURCES = [
    {
        keyField: 'item_ineligibility_reason_key',
        duplicateSourceField: '_item_ineligibility_reason_source_key',
        buildClientKey: buildPolicyItemIneligibilityReasonClientKey,
    },
] as const;

export function buildDuplicatedPolicyLocalizationKeys(
    clientId: string,
    sourcePolicy: Pick<
        IPolicyRule,
        (typeof LOCALIZED_POLICY_FIELD_SOURCES)[number]['keyField']
    >
): Partial<IPolicyRule> {
    const result: Partial<IPolicyRule> = {};

    LOCALIZED_POLICY_FIELD_SOURCES.forEach(
        ({ keyField, duplicateSourceField, buildClientKey }) => {
            result[keyField] = buildClientKey(clientId);
            result[duplicateSourceField] = sourcePolicy[keyField] ?? null;
        }
    );

    return result;
}

export interface IPolicyRuleAvailability {
    used_for: USED_FOR_OPTIONS;
}

export interface IPolicyFee {
    id: number,
    is_charged: boolean,
    charge_tax: boolean|null,
    fee_type: FEE_TYPE|null, /** this field can be null in DB and it means - shipping label type */
    amount_type: AMOUNT_TYPE,
    amount: number,
    _has_changes?: boolean,
    _deleted: boolean,
}

export enum CONDITION_OPERATORS {
    OP_SMALLER = 'OP_SMALLER',
    OP_SMALLER_OR_EQUAL = 'OP_SMALLER_OR_EQUAL',
    OP_EQUAL = 'OP_EQUAL',
    OP_GREATER = 'OP_GREATER',
    OP_GREATER_OR_EQUAL = 'OP_GREATER_OR_EQUAL',
    OP_NOT_EQUAL = 'OP_NOT_EQUAL',
    OP_CONTAINS = 'OP_CONTAINS',
    OP_NOT_CONTAINS = 'OP_NOT_CONTAINS',
    OP_IS = 'OP_IS',
    OP_IS_STRICT = 'OP_IS_STRICT',
    OP_IS_NOT = 'OP_IS_NOT',
    OP_ONLY_ONE_OF = 'OP_ONLY_ONE_OF',
    OP_ONLY_ONE_OF_THOSE = 'OP_ONLY_ONE_OF_THOSE',
    OP_ONE_OR_MORE_OF = 'OP_ONE_OR_MORE_OF',
    OP_ALL_OF = 'OP_ALL_OF',
    OP_NONE_OF = 'OP_NONE_OF',
    OP_ON = 'OP_ON',
    OP_BETWEEN = 'OP_BETWEEN',
    OP_BETWEEN_INTEGER = 'OP_BETWEEN_INTEGER',
    OP_HAS_ANY_VALUE = 'OP_HAS_ANY_VALUE',
    OP_IS_EMPTY = 'OP_IS_EMPTY',
}

export enum CONDITION_GROUP_OPERATOR {
    OP_AND = 'OP_AND',
    OP_OR = 'OP_OR',
}

export interface IPolicyCondition {
    id: number,
    field: string,
    operator: CONDITION_OPERATORS,
    compare_value: string,
    _deleted?: boolean,
    _has_changes?: boolean,
}

export interface IPolicyConditionGroup {
    id: number,
    conditions_operator: CONDITION_GROUP_OPERATOR,
    is_eligible: boolean,
    policy_rule_id: number | null,
    policy_rule_id_rma_level: number | null,
    policy_rule_id_order_level: number | null,
    policy_r_id_customer_lvl: number | null,
    policy_rule_conditions: IPolicyCondition[],

    _deleted?: boolean,
    _has_changes?: boolean,
}

export interface IPolicyAddressRequirements {
    email: {
        display: boolean;
        required: boolean;
    };
    phone: {
        display: boolean;
        required: boolean;
    };
    country: {
        display: boolean;
        required: boolean;
    };
    state: {
        display: boolean;
        required: boolean;
    };
    address_line: {
        display: boolean;
        required: boolean;
    };
    apartment: {
        display: boolean;
    };
    zip: {
        display: boolean;
        required: boolean;
    };
    city: {
        display: boolean;
        required: boolean;
    };
}

export enum POLICY_RULE_CATEGORY {
    ORDER_MODIFICATION = 'order_modification',
    EXCHANGE = 'exchange',
    RETURN = 'return',
}

export enum DISPLAY_FEE_OPTION {
    FREE = 'FREE',
    INCLUDE_ZERO_FREE = 'INCLUDE_ZERO_FREE',
    DO_NOT_MENTION_FEES = 'DO_NOT_MENTION_FEES',
}

/** all possible types we have in policy_rule_fees field fee_type and fee_type can also be NULL */
export enum FEE_TYPE {
    COST = 'FEE_TYPE_COST',
    FREE = 'FEE_TYPE_FREE',
    COST_PLUS_ITEM_FEE = 'FEE_TYPE_COST_PLUS_ITEM_FEE',
    COST_PLUS_SHIPMENT_FEE = 'FEE_TYPE_COST_PLUS_SHIPMENT_FEE',
    CANCELLATION_FEE = 'FEE_TYPE_CANCELLATION_FEE',
    RESTOCKING_FEE = 'FEE_TYPE_RESTOCKING_FEE',
    SHIPMENT_FEE = 'FEE_TYPE_SHIPMENT_FEE',
    LABEL_NOT_PROVIDED = 'FEE_TYPE_LABEL_NOT_PROVIDED',
    RETURN_FEE = 'FEE_TYPE_RETURN_FEE',
    RESERVE_ITEMS_FEE = 'FEE_TYPE_RESERVE_ITEMS_FEE',
}

export const FEE_TYPE_FREE = 'FREE';

/** subgroup from FEE_TYPE of all possible fee_types for shipping lable (cost) + NULL */
export const SHIPPING_LABEL_FEE_TYPES = [
    FEE_TYPE.SHIPMENT_FEE,
    FEE_TYPE.COST_PLUS_SHIPMENT_FEE,
    FEE_TYPE.COST_PLUS_ITEM_FEE,
    FEE_TYPE.COST,
    FEE_TYPE.LABEL_NOT_PROVIDED,
];

/** Fee types that expose amount inputs. {@link FEE_TYPE.COST} (carrier cost only) does not. */
export const FEE_TYPES_REQUIRING_AMOUNT_INPUT: readonly FEE_TYPE[] = [
    FEE_TYPE.COST_PLUS_ITEM_FEE,
    FEE_TYPE.COST_PLUS_SHIPMENT_FEE,
    FEE_TYPE.CANCELLATION_FEE,
    FEE_TYPE.RESTOCKING_FEE,
    FEE_TYPE.SHIPMENT_FEE,
    FEE_TYPE.LABEL_NOT_PROVIDED,
    FEE_TYPE.RETURN_FEE,
    FEE_TYPE.RESERVE_ITEMS_FEE,
];

export const FEE_TYPE_INCLUDS_COST = [
    FEE_TYPE.COST_PLUS_SHIPMENT_FEE,
    FEE_TYPE.COST_PLUS_ITEM_FEE,
    FEE_TYPE.COST,
];

export const FEE_TYPE_MAP = {
    [FEE_TYPE.COST]: 'Label fee: Cost',
    [FEE_TYPE.FREE]: 'Free',
    [FEE_TYPE.COST_PLUS_ITEM_FEE]: 'Label fee: Cost + per item fee',
    [FEE_TYPE.COST_PLUS_SHIPMENT_FEE]: 'Label fee: Cost + per shipment fee',
    [FEE_TYPE.CANCELLATION_FEE]: 'Cancellation Fee',
    [FEE_TYPE.RESTOCKING_FEE]: 'Restocking fee',
    [FEE_TYPE.SHIPMENT_FEE]: 'Label fee: Fixed amount',
    [FEE_TYPE.LABEL_NOT_PROVIDED]: 'Label not provided',
    [FEE_TYPE.RETURN_FEE]: 'Return Fee (per RMA)',
    [FEE_TYPE.RESERVE_ITEMS_FEE]: 'Reserve Item Fee',
}

export enum CONDITION_GROUP_LEVELS {
    ORDER = 'GROUP_ORDER_LEVEL',
    CUSTOMER = 'GROUP_CUSTOMER_LEVEL',
    ITEM = 'GROUP_ITEMS_LEVEL',
    RMA = 'GROUP_RMA_LEVEL',
}

export interface IPolicyRulesSettingsPreset {
    conditions: IConditionPreset,
    return_reasons: IReturnReasonPreset[],
    return_methods: IReturnMethodPreset[],
    resolutions: IResolutionCategoryPreset[],
    aggregators_info: IAggregatorInfo[],
    policy_category_to_resolution_ids_map: IPolicyCategoryToResolutionMap,
    has_global_e_direct_refund_integration: boolean,
}

export interface IPolicyCategoryToResolutionMap {
    [POLICY_RULE_CATEGORY.EXCHANGE]: IPolicyCategoryResolutionIds,
    [POLICY_RULE_CATEGORY.ORDER_MODIFICATION]: IPolicyCategoryResolutionIds,
    [POLICY_RULE_CATEGORY.RETURN]: IPolicyCategoryResolutionIds,
}

interface IPolicyCategoryResolutionIds {
    exclude_resolution_ids: RESOLUTION_ID[],
    include_resolution_ids: RESOLUTION_ID[],
}

export interface IResolutionCategoryPreset extends Omit<IResolutionCategory, 'list'> {
    category: RESOLUTION_CATEGORY,
    list: IResolutionPreset[],
}

export interface IResolutionPreset extends Partial<IResolution> {
    id: number,
    internal_name: string,
    resolution_id: RESOLUTION_ID,
    resolution_type: RESOLUTION_TYPE,
    instant_refund_provider: INSTANT_REFUND_PROVIDER | null,
    present_more_expensive: boolean,
    indicate_less_expensive: boolean,
}

export interface IReturnReasonPreset {
    id: number;
    display_name: string;
    is_enabled: boolean;
}

interface IReturnLabelSettingsPreset extends Partial<IReturnLabelSettings> {
    shipping_connection: IShippingConnection;
    shipping_connection_id: number;
    return_method_id: number;
    id: number;
    carrier_code: string;
    service_code: string;
}

interface IDropOffSettingsPreset extends Partial<IDropOffSettings> {
    shipping_connection: IShippingConnection;
    shipping_connection_id: number;
    return_method_id: number;
    id: number;
}

/** this is partial interface of IReturnMethod with changed property type of return_label_setting */
export interface IReturnMethodPreset extends Partial<Omit<IReturnMethod, 'return_label_setting'|'drop_off_setting'>> {
    id: number;
    name: string;
    type: RETURN_METHOD_TYPE;
    return_label_setting?: IReturnLabelSettingsPreset;
    drop_off_setting?: IDropOffSettingsPreset;
    is_hidden: boolean;
}

export interface ICompareValueItem {
    name: string,
    value?: string,
    code?: string,
    countries?: ICompareValueItem[],
    provinces?: { name: string, code: string }[],
    cities?: { name: string, code: string, provinceCode?: string }[],
}

export interface IConditionFieldPreset {
    value: string,
    label: string,
    compare_value_type: string,
    tooltip: string,
    operators: IOption[],
    compare_value_data: string[] | ICompareValueItem[],
    /** API preset field: codes that enable semantic grouping (e.g. EU). */
    country_group_codes?: string[],
    text: string,
    input_placeholder: string,
}

export type IConditionLimitationRules = {
    [key in RESOLUTION_TYPE]: {
        is_enabled: boolean,
        allowed_conditions: string[],
        not_allowed_conditions: string[],
    }
}

export interface IConditionByLevelPreset {
    group: IConditionPresetGroup,
    conditions: {
        [key: string]: IConditionFieldPreset
    },
    enabled: boolean,
    limitations_by_resolution_type: IConditionLimitationRules
}

export interface IConditionPreset {
    [CONDITION_GROUP_LEVELS.ORDER]: {
        preset: IConditionByLevelPreset
    },
    [CONDITION_GROUP_LEVELS.CUSTOMER]: {
        preset: IConditionByLevelPreset
    },
    [CONDITION_GROUP_LEVELS.RMA]: {
        preset: IConditionByLevelPreset
    },
    [CONDITION_GROUP_LEVELS.ITEM]: {
        preset: IConditionByLevelPreset
    },
}

export interface IConditionPresetGroup {
    save_key: string,
    description: string,
    eligibility: { value: boolean, label: string }[],
    eligibility_description: string,
    operators: IOption[],
    conditions_title: string
}

export enum POLICY_CONDITION_COMPARE_VALUE_TYPE {
    KEY_VALUE = 'key_value',
    DATE = 'date',
    WINDOW = 'window',
    WEIGHT = 'weight',
    INTEGER_BETWEEN = 'integer_between',
    PRICE = 'price',
    STRING = 'string',
    TAG = 'tag',
    INTEGER = 'integer',
    LOCATION = 'location',
    RETURN_REASON = 'return_reason',
    CUSTOMER_STATE = 'customer_state',
    PURCHASED = 'purchased',
    FLOAT = 'float',
}

export enum AMOUNT_TYPE {
    FIXED = 'AMOUNT_TYPE_FIXED',
    ITEM_PERCENTAGE_CATALOG_PRICE = 'AMOUNT_TYPE_ITEM_PERCENTAGE_CATALOG_PRICE',
    ITEM_PERCENTAGE_PAID_PRICE = 'AMOUNT_TYPE_ITEM_PERCENTAGE_PAID_PRICE',
}
export const FEE_AMOUNT_OPTIONS = [
    {
        value: AMOUNT_TYPE.FIXED,
        label: 'Fixed amount'
    },
    {
        value: AMOUNT_TYPE.ITEM_PERCENTAGE_CATALOG_PRICE,
        label: 'Percentage of item\'s catalog price'
    },
    {
        value: AMOUNT_TYPE.ITEM_PERCENTAGE_PAID_PRICE,
        label: 'Percentage of item\'s paid price'
    },
];


export const FEE_AMOUNT_OPTIONS_TRANSLATED = [
    {
        value: AMOUNT_TYPE.FIXED,
        label: 'FIXED_AMOUNT'
    },
    {
        value: AMOUNT_TYPE.ITEM_PERCENTAGE_CATALOG_PRICE,
        label: 'ITEM_PERCENTAGE_CATALOG_PRICE'
    },
    {
        value: AMOUNT_TYPE.ITEM_PERCENTAGE_PAID_PRICE,
        label: 'ITEM_PERCENTAGE_PAID_PRICE'
    },
];



export interface IConditionError {
    type: CONDITION_ERROR,
    body: CONDITION_ERROR_MESSAGE
}

export enum CONDITION_ERROR {
    DUPLICATED = 'duplicated',
    CONTRADICT = 'contradict',
    INCOMPLETE = 'incomplete'
}
export enum CONDITION_ERROR_MESSAGE{
    DUPLICATED = 'Duplicates - This condition is a duplicate of the marked condition',
    CONTRADICT = 'Contradiction - This condition contradicts the marked condition',
    INCOMPLETE = 'Incomplete condition - one or more fields are without a valid value'
}

export interface IReturnMethodNoteData {
    type: NOTE_TYPE,
    aggregator: string | undefined,
    carrier: string | undefined,
    service: string | undefined,
}
